import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'
import {
  ADSENSE_AD_UNIT_ID,
  ADSENSE_CLIENT_ID,
  ADSENSE_ENABLED,
  ADSENSE_SCRIPT_SRC,
} from '../config/adsense'
import { adsAllowedOn } from '../seo/routeMeta'

declare global {
  interface Window {
    adsbygoogle?: unknown[]
  }
}

/**
 * Google AdSense display unit, labeled "Advertisement".
 *
 * Renders nothing unless all of these hold:
 *   - the current route is flagged ads:true in src/seo/routes.json
 *     (content pages only; never thin templates or policy pages)
 *   - a real ad-unit id is configured (ADSENSE_AD_UNIT_ID)
 *   - the script did not fail to load (ad blockers)
 * When AdSense reports the unit unfilled, the whole block collapses so no empty
 * box is left on the page. It collapses by height/visibility, never
 * display:none, because adsbygoogle reads the slot width.
 *
 * Affiliate partner links are a separate component (PartnerSupportStrip) with
 * their own "Affiliate links" label; they are never placed inside this block.
 */
export function AdSenseSlot({ className = '' }: { className?: string }) {
  const { pathname } = useLocation()
  const pushed = useRef(false)
  const insRef = useRef<HTMLModElement>(null)
  const [failed, setFailed] = useState(false)
  const [unfilled, setUnfilled] = useState(false)
  const active = ADSENSE_ENABLED && Boolean(ADSENSE_AD_UNIT_ID) && adsAllowedOn(pathname) && !failed

  useEffect(() => {
    if (!active) return

    if (!document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]')) {
      const script = document.createElement('script')
      script.async = true
      script.crossOrigin = 'anonymous'
      script.src = ADSENSE_SCRIPT_SRC
      script.onerror = () => setFailed(true)
      document.head.appendChild(script)
    }

    if (!pushed.current) {
      try {
        ;(window.adsbygoogle = window.adsbygoogle || []).push({})
        pushed.current = true
      } catch {
        setFailed(true)
      }
    }

    const ins = insRef.current
    if (!ins || typeof MutationObserver === 'undefined') return
    const check = () => {
      if (ins.getAttribute('data-ad-status') === 'unfilled') setUnfilled(true)
    }
    const observer = new MutationObserver(check)
    observer.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })
    check()
    return () => observer.disconnect()
  }, [active])

  if (!active) return null

  const collapsed: CSSProperties = unfilled
    ? { height: 0, minHeight: 0, margin: 0, padding: 0, borderWidth: 0, overflow: 'hidden', visibility: 'hidden' }
    : {}

  return (
    <aside className={className} style={collapsed} data-adsense-slot aria-label="Advertisement">
      <p className="mb-1 text-[10px] uppercase tracking-wider text-gray-500">Advertisement</p>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', minHeight: unfilled ? 0 : 90 }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={ADSENSE_AD_UNIT_ID}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </aside>
  )
}
